import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  apiGet, API_BASE,
  clearSessionToken, readSessionToken,
  saveCachedUser, readCachedUser, clearCachedUser,
} from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // OPTIMISTIC HYDRATION: if we have a cached user in localStorage, restore it
  // synchronously on first render so ProtectedRoute doesn't flash to /login
  // while /auth/me is in flight. The cached user is validated immediately
  // afterwards via checkAuth(); only a genuine 401 wipes the cache.
  const [user, setUserRaw] = useState(() => {
    const cached = readCachedUser();
    const tok = readSessionToken();
    // Only trust the cache if we ALSO have a session token — otherwise
    // we're in a logged-out state and the cache is stale.
    return cached && tok ? cached : null;
  });
  const [loading, setLoading] = useState(true);
  const validatedRef = useRef(false);

  // setUser wrapper that keeps the localStorage cache in sync. It accepts an
  // updater function too (setUser(prev => ...)), which onboarding relies on —
  // the old version cached the function itself and silently corrupted the cache.
  const setUser = useCallback((u) => {
    if (typeof u === 'function') {
      setUserRaw((prev) => {
        const next = u(prev);
        if (next) saveCachedUser(next); else clearCachedUser();
        return next;
      });
      return;
    }
    if (u) {
      saveCachedUser(u);
    } else {
      clearCachedUser();
    }
    setUserRaw(u);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const userData = await apiGet('/auth/me');
      // Successful validation → refresh both context state and the cache.
      setUser(userData);
    } catch (err) {
      if (err?.isNetworkError) {
        // Transient failure (offline, DNS, blocked CORS preflight, server
        // hiccup, cold pod). Do NOT clear the user — keep them logged in
        // optimistically from the cache. They'll re-validate on next call.
        // This eliminates the "logged in then kicked" symptom on flaky
        // production networks.
        return;
      }
      if (err?.status === 401 || err?.message === 'UNAUTHORIZED') {
        // Genuine auth failure → user is truly logged out, wipe everything.
        clearSessionToken();
        clearCachedUser();
        setUserRaw(null);
        return;
      }
      // Any other error: leave user state alone, just log it.
      // eslint-disable-next-line no-console
      console.warn('checkAuth: unexpected error, keeping cached user', err);
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // Guard against double-validation in StrictMode dev double-mount.
    if (validatedRef.current) return;
    validatedRef.current = true;
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run exactly once on mount — checkAuth ref is stable but we lock anyway.

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/home';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {}
    clearSessionToken();
    clearCachedUser();
    setUserRaw(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
